import axios from 'axios'

const IMPERSONATION_TOKEN_KEY = 'impersonation_token'
const IMPERSONATION_SESSION_KEY = 'impersonation_session'

/** Query param carrying the single-use handoff code from the operations app. */
const HANDOFF_PARAM = '_imp'

export { IMPERSONATION_TOKEN_KEY, IMPERSONATION_SESSION_KEY, HANDOFF_PARAM }

export interface ImpersonationSessionInfo {
  sessionId: string
  targetUser: {
    id: string
    email: string
    name: string
    type: string
  }
  expiresAt: string
}

/** Drop per-tab impersonation tokens so API calls use the platform JWT in localStorage. */
export function clearImpersonationClientState(): void {
  if (typeof window === 'undefined') {
    return
  }
  sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY)
  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY)
}

/** The handoff code this tab was opened with, if any. */
export function readHandoffCode(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  return new URL(window.location.href).searchParams.get(HANDOFF_PARAM)
}

function stripHandoffParam(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete(HANDOFF_PARAM)
  window.history.replaceState({}, '', url.pathname + url.search + url.hash)
}

/**
 * Exchange the handoff code for this tab's impersonation token.
 *
 * The operations app and the provider console are separate origins, so the
 * session cannot be handed over through storage. The operations app instead
 * issues a single-use code, and this tab — the one that will actually use the
 * token — redeems it. Must complete before anything reads the auth state, so
 * the tab never renders as the admin before switching to the target user.
 *
 * Uses a bare axios instance: the shared client's 401 handling would treat a
 * rejected code as an expired session and try to refresh it.
 */
export async function redeemImpersonationHandoff(code: string): Promise<ImpersonationSessionInfo> {
  try {
    const response = await axios.post('/ui/operation/impersonation-session/redeem', { code })
    const { impersonationToken, sessionId, targetUser, expiresAt } = response.data
    const session: ImpersonationSessionInfo = { sessionId, targetUser, expiresAt }

    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, impersonationToken)
    sessionStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(session))

    return session
  } finally {
    // Always strip the code, redeemed or not: it is single-use, so leaving it in
    // the URL only risks it being reloaded, shared or logged.
    stripHandoffParam()
  }
}
